import React from 'react';
import { Button, Checkbox, Text, Tooltip, makeStyles, shorthands } from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import { AlwaysOnScrollBarStyles, customColors, customFonts } from '../../../../../styles';
import type { KnowledgeSourceAgentOption } from '../../../hooks/useKnowledgeSources';

const useStyles = makeStyles({
    menu: {
        position: 'absolute',
        left: 0,
        minWidth: '260px',
        maxWidth: '320px',
        zIndex: 10,
        backgroundColor: customColors.white,
        ...shorthands.borderRadius('6px'),
        ...shorthands.padding('8px'),
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
    },
    menuDropDown: {
        top: 'calc(100% + 6px)',
    },
    menuDropUp: {
        bottom: 'calc(100% + 6px)',
    },
    actionsRow: {
        display: 'flex',
        justifyContent: 'flex-start',
        marginBottom: '8px',
    },
    selectAllButton: {
        height: '28px',
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        fontWeight: '700',
        textDecoration: 'underline',
        color: customColors.protivitiDarkBlue,
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '10px'),
        backgroundColor: customColors.white,
    },
    scrollArea: {
        maxHeight: '208px',
        overflowY: 'auto',
        ...AlwaysOnScrollBarStyles,
    },
    menuList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        paddingRight: '4px',
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        ...shorthands.padding('4px', '2px'),
        backgroundColor: customColors.lightBlueGrayBackground3,
        ...shorthands.borderRadius('5px'),
    },
    checkbox: {
        flexGrow: 1,
        minWidth: 0,
        '& label': {
            fontFamily: customFonts.Lato,
            fontSize: '13px',
            color: customColors.protivitiDarkBlue,
        },
        '& input:checked + .fui-Checkbox__indicator': {
            backgroundColor: customColors.protivitiOrange,
            ...shorthands.borderColor(customColors.protivitiOrange),
            color: customColors.white,
        },
        '& input:checked:hover + .fui-Checkbox__indicator': {
            backgroundColor: customColors.protivitiOrange,
            ...shorthands.borderColor(customColors.protivitiOrange),
            color: customColors.white,
        },
        '& input:focus-visible + .fui-Checkbox__indicator': {
            ...shorthands.borderColor(customColors.protivitiOrange),
        },
    },
    infoButton: {
        minWidth: '28px',
        width: '28px',
        height: '28px',
        color: customColors.protivitiDarkBlue,
        ...shorthands.borderRadius('4px'),
    },
    emptyState: {
        fontFamily: customFonts.Lato,
        fontSize: '13px',
        color: customColors.protivitiDarkBlue,
    },
});

interface KnowledgeSourcesMenuProps {
    isDropUp: boolean;
    agents: KnowledgeSourceAgentOption[];
    selectedAgentIds: string[];
    areAllSelected: boolean;
    onToggleSelectedAgent: (agentId: string) => void;
    onToggleAllAgents: () => void;
}

const KnowledgeSourcesMenu: React.FC<KnowledgeSourcesMenuProps> = ({
    isDropUp,
    agents,
    selectedAgentIds,
    areAllSelected,
    onToggleSelectedAgent,
    onToggleAllAgents,
}) => {
    const styles = useStyles();

    if (agents.length === 0) {
        return (
            <div className={`${styles.menu} ${isDropUp ? styles.menuDropUp : styles.menuDropDown}`}>
                <Text className={styles.emptyState}>No knowledge sources are available.</Text>
            </div>
        );
    }

    return (
        <div className={`${styles.menu} ${isDropUp ? styles.menuDropUp : styles.menuDropDown}`}>
            <div className={styles.actionsRow}>
                <Button appearance="transparent" className={styles.selectAllButton} onClick={onToggleAllAgents}>
                    {areAllSelected ? 'Deselect All' : 'Select All'}
                </Button>
            </div>
            <div className={styles.scrollArea}>
                <div className={styles.menuList}>
                    {agents.map((agent) => {
                        const tooltipContent = agent.description ?? 'No additional information available.';

                        return (
                            <div key={agent.agentId} className={styles.menuItem}>
                                {/* TODO: for now agent name and id are the same, but we want to use the right name property once we have spaces thing fixed*/}
                                <Checkbox
                                    className={styles.checkbox}
                                    checked={selectedAgentIds.includes(agent.agentId)}
                                    label={agent.name}
                                    onChange={() => onToggleSelectedAgent(agent.agentId)}
                                />

                                {/* TODO: confirm how we want this bahevior. do we want it as hover tooltip liek this or we nedd more info? */}
                                <Tooltip content={tooltipContent} relationship="description">
                                    <Button
                                        appearance="transparent"
                                        className={styles.infoButton}
                                        icon={<InfoRegular />}
                                        aria-label={`More information about ${agent.name}`}
                                    />
                                </Tooltip>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default KnowledgeSourcesMenu;
